import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function Login() {
    const { login, requestMagicLink, user, error, setError } = useAuth();
    const [mode, setMode] = useState("magic"); // 'magic' | 'password'
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => { setError?.(""); }, [setError, mode]);
    useEffect(() => {
        if (user && user.id) {
            const dest = location.state?.from || "/";
            navigate(dest, { replace: true });
        }
    }, [user, navigate, location.state]);

    const onSubmitMagic = async (e) => {
        e.preventDefault();
        if (!email) { toast.error("Enter your email"); return; }
        setBusy(true);
        const ok = await requestMagicLink(email);
        setBusy(false);
        if (ok) setSent(true);
    };

    const onSubmitPassword = async (e) => {
        e.preventDefault();
        setBusy(true);
        const ok = await login(email, password);
        setBusy(false);
        if (ok) navigate(location.state?.from || "/", { replace: true });
    };

    const resendMagic = async () => {
        setBusy(true);
        const ok = await requestMagicLink(email);
        setBusy(false);
        if (ok) toast.success("Link resent. Check your inbox.");
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-ink-50">
            {/* Editorial side */}
            <div className="hidden lg:flex flex-col justify-between p-12 bg-haevn-700 text-white relative overflow-hidden grain">
                <Logo tone="dark" className="h-7" showSub />
                <div className="max-w-md">
                    <div className="text-[10px] uppercase tracking-[0.25em] opacity-70 font-mono mb-4">
                        Internal Training Operating System
                    </div>
                    <h1 className="font-editorial text-4xl xl:text-5xl leading-[1.05] mb-6">
                        Train with intent.<br />
                        <span className="opacity-70">Book with conviction.</span>
                    </h1>
                    <p className="text-sm text-white/70 leading-relaxed">
                        A focused environment for HAEVN trainees to master the brand, the programs they support,
                        and the conversations that turn intent into action.
                    </p>
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] opacity-50 font-mono">
                    Authorized personnel only
                </div>
            </div>

            {/* Form side */}
            <div className="flex flex-col justify-center p-8 md:p-16">
                <div className="w-full max-w-sm mx-auto">
                    <div className="lg:hidden mb-8">
                        <Logo tone="light" className="h-7" showSub />
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3">Sign in</div>
                    <h2 className="font-editorial text-3xl text-ink-900 mb-1">Welcome back.</h2>
                    <p className="text-sm text-ink-500 mb-8">
                        {mode === "magic"
                            ? "We'll email you a one-time link — no password required."
                            : "Sign in with your email and password."}
                    </p>

                    {/* Magic link form */}
                    {mode === "magic" && !sent && (
                        <form onSubmit={onSubmitMagic} className="space-y-4" data-testid="magic-link-form">
                            <div>
                                <label className="block text-[11px] uppercase tracking-[0.15em] text-ink-500 font-mono mb-1.5">Email</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    data-testid="login-email-input"
                                    className="w-full bg-white border border-ink-300 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-haevn-700 transition-colors"
                                    placeholder="you@haevn.com"
                                    autoFocus
                                />
                            </div>
                            {error && (
                                <div className="text-xs text-clay-500 bg-clay-50 border border-clay-50 px-3 py-2 rounded-sm" data-testid="login-error">
                                    {error}
                                </div>
                            )}
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="magic-link-submit-btn"
                                className="w-full bg-haevn-700 text-white py-2.5 rounded-sm hover:bg-haevn-900 transition-colors font-medium text-sm disabled:opacity-50"
                            >
                                {busy ? "Sending…" : "Email me a sign-in link"}
                            </button>
                        </form>
                    )}

                    {/* Sent state */}
                    {mode === "magic" && sent && (
                        <div className="space-y-4" data-testid="magic-link-sent-state">
                            <div className="border border-haevn-100 bg-haevn-50 p-5 rounded-sm">
                                <div className="text-[10px] uppercase tracking-[0.15em] text-haevn-700 font-mono mb-1.5">Check your inbox</div>
                                <p className="text-sm text-ink-900 mb-2">
                                    If <span className="font-medium">{email}</span> is authorized, we just emailed a sign-in link.
                                </p>
                                <p className="text-xs text-ink-500">
                                    The link expires in 15 minutes. Don&apos;t see it? Check spam, or resend below.
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={resendMagic}
                                    disabled={busy}
                                    data-testid="magic-link-resend-btn"
                                    className="text-[11px] uppercase tracking-[0.15em] text-haevn-700 hover:text-haevn-900 font-mono"
                                >
                                    {busy ? "Resending…" : "Resend link"}
                                </button>
                                <span className="text-ink-300">·</span>
                                <button
                                    onClick={() => { setSent(false); setEmail(""); }}
                                    data-testid="magic-link-change-email-btn"
                                    className="text-[11px] uppercase tracking-[0.15em] text-ink-500 hover:text-ink-900 font-mono"
                                >
                                    Use a different email
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Password fallback form */}
                    {mode === "password" && (
                        <form onSubmit={onSubmitPassword} className="space-y-4" data-testid="login-form">
                            <div>
                                <label className="block text-[11px] uppercase tracking-[0.15em] text-ink-500 font-mono mb-1.5">Email</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    data-testid="login-email-input"
                                    className="w-full bg-white border border-ink-300 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-haevn-700 transition-colors"
                                    placeholder="you@haevn.com"
                                />
                            </div>
                            <div>
                                <label className="block text-[11px] uppercase tracking-[0.15em] text-ink-500 font-mono mb-1.5">Password</label>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    data-testid="login-password-input"
                                    className="w-full bg-white border border-ink-300 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-haevn-700 transition-colors"
                                    placeholder="••••••••"
                                />
                            </div>
                            {error && (
                                <div className="text-xs text-clay-500 bg-clay-50 border border-clay-50 px-3 py-2 rounded-sm" data-testid="login-error">
                                    {error}
                                </div>
                            )}
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="login-submit-btn"
                                className="w-full bg-haevn-700 text-white py-2.5 rounded-sm hover:bg-haevn-900 transition-colors font-medium text-sm disabled:opacity-50"
                            >
                                {busy ? "Signing in…" : "Sign in"}
                            </button>
                        </form>
                    )}

                    {/* Mode toggle */}
                    <div className="mt-8 pt-6 border-t border-ink-200 text-center">
                        {mode === "magic" ? (
                            <button
                                type="button"
                                onClick={() => { setMode("password"); setSent(false); }}
                                data-testid="login-use-password-btn"
                                className="text-sm text-ink-500 hover:text-haevn-700 underline underline-offset-4"
                            >
                                Use password instead
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => { setMode("magic"); setPassword(""); }}
                                data-testid="login-use-magic-link-btn"
                                className="text-sm text-ink-500 hover:text-haevn-700 underline underline-offset-4"
                            >
                                Use a magic link instead
                            </button>
                        )}
                    </div>
                    <div className="mt-3 text-center">
                        <span className="text-sm text-ink-500">HAEVN agency partner? </span>
                        <Link to="/signup" data-testid="login-to-partner-signup-link" className="text-sm text-haevn-700 hover:text-haevn-900 underline underline-offset-4 font-medium">Create an agency account</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
